import React, { useState } from "react"
import { Box, Text } from "@chakra-ui/react"
import { Control, useController } from "react-hook-form"
import Icon from "components/elements/Icon"
import Menu, { MenuItem } from "components/dialogs/Menu"
import Button from "components/Buttons/Button"
import { uploadTextMedia } from "api/texts"
import { WhatsAppMediaType } from "sharedTypes"
import { errorToast, handleError } from "utilities/toasts"
import { isImageUrl } from "utilities/whatsappTemplates"

const MEDIA_ACCEPT: Record<WhatsAppMediaType, string> = {
  image: "image/jpeg,image/png",
  document: "application/pdf,.doc,.docx",
}

const MAX_FILE_SIZE = 5 * 1024 * 1024

type Props = {
  name: string
  control: Control<any>
  mediaType?: WhatsAppMediaType
  onSave?: () => void
  eventId?: string
  textMessageId?: string
}

const AttachmentButton = ({
  name, control, mediaType, onSave, eventId, textMessageId,
}: Props) => {
  const { field } = useController({ name, control, defaultValue: "" })
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const hasFile = Boolean(field.value)
  const fileName = hasFile ? field.value.split("/").pop() : null
  const accept = mediaType ? MEDIA_ACCEPT[mediaType] : `${MEDIA_ACCEPT.image},${MEDIA_ACCEPT.document}`

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !eventId || !textMessageId) return

    if (file.size > MAX_FILE_SIZE) {
      errorToast({ title: "File is too large. Maximum size is 5 MB." })

      return
    }

    setIsUploading(true)
    try {
      const { data } = await uploadTextMedia(eventId, textMessageId, file)
      field.onChange(data.link)
      onSave?.()
    } catch (error) {
      handleError(error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = () => {
    field.onChange("")
    onSave?.()
  }

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={accept}
      style={{ display: "none" }}
      onChange={handleFileChange}
    />
  )

  if (isUploading) {
    return (
      <Button variant="outline" size="sm" isLoading loadingText="Uploading...">
        Uploading...
      </Button>
    )
  }

  if (!hasFile) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
        >
          Upload
        </Button>
        {fileInput}
      </>
    )
  }

  const image = isImageUrl(field.value)

  return (
    <>
      <Menu
        placement="bottom"
        button={(
          <Box
            display="inline-flex"
            alignItems="center"
            gap={2}
            h={8}
            px={3}
            bg="gray.100"
            borderWidth={1}
            borderColor="gray.300"
            borderRadius="md"
            fontSize="sm"
            fontWeight="medium"
            color="gray.700"
            cursor="pointer"
            _hover={{ bg: "gray.200" }}
          >
            {image ? (
              <Box
                as="img"
                src={field.value}
                alt={fileName || "attachment"}
                h={5}
                w={5}
                objectFit="cover"
                borderRadius="sm"
                flexShrink={0}
              />
            ) : (
              <Icon icon="file-icon" size={4} color="gray.500" />
            )}
            <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="150px">
              {fileName}
            </Text>
            <Icon icon="down-arrow" size={3} color="gray.500" />
          </Box>
        )}
        menuItems={[
          <MenuItem
            key="view"
            onClick={() => window.open(field.value, "_blank")}
          >
            View attachment
          </MenuItem>,
          <MenuItem
            key="replace"
            onClick={() => fileInputRef.current?.click()}
          >
            Replace
          </MenuItem>,
          <MenuItem
            key="remove"
            onClick={handleRemove}
            color="red.700"
          >
            Remove
          </MenuItem>,
        ]}
      />
      {fileInput}
    </>
  )
}

export default AttachmentButton
